import { CircleCheck, ExternalLink, RefreshCw, Server } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { ErrorMessage, Loading, PageHeader } from '../components';

export function SystemPage() {
  const [version, setVersion] = useState<{ version: string; commit: string }>();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    void Promise.all([
      api<{ version: string; commit: string }>('/version', { signal: controller.signal }),
      api<{ status: string }>('/health/ready', { signal: controller.signal }),
    ])
      .then(([info, health]) => {
        setVersion(info);
        setReady(health.status === 'ready');
        setError(undefined);
      })
      .catch((caught) => {
        if (!controller.signal.aborted) {
          setError(caught);
          setReady(false);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [refresh]);
  return (
    <>
      <PageHeader
        title="System"
        description="Verfügbarkeit und technische Informationen zu Control."
        actions={
          <button
            className="button-secondary"
            disabled={loading}
            onClick={() => setRefresh((value) => value + 1)}
          >
            <RefreshCw size={16} />
            Aktualisieren
          </button>
        }
      />
      <ErrorMessage error={error} />
      <div className="panel p-6">
        {loading ? (
          <Loading>Verfügbarkeit wird geprüft…</Loading>
        ) : (
          <div className="flex items-center gap-4">
            <div
              className={`grid h-12 w-12 place-items-center rounded-xl ${ready ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
            >
              {ready ? <CircleCheck size={24} /> : <Server size={24} />}
            </div>
            <div>
              <h2 className="font-semibold">
                {ready ? 'Control ist betriebsbereit' : 'Control ist derzeit nicht betriebsbereit'}
              </h2>
              <p className="mt-1 text-sm text-muted">
                {ready
                  ? 'Die API meldet einen erfolgreichen Bereitschaftscheck.'
                  : 'Der Bereitschaftscheck ist fehlgeschlagen. Bitte erneut prüfen.'}
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <section className="panel p-6">
          <h2 className="section-title">Version</h2>
          <div className="text-xl font-semibold">{version?.version ?? 'Nicht verfügbar'}</div>
          <details className="technical">
            <summary>Build-Details</summary>
            <p className="mt-3 break-all font-mono">
              Commit: {version?.commit ?? 'Nicht verfügbar'}
            </p>
          </details>
        </section>
        <section className="panel p-6">
          <h2 className="section-title">API-Dokumentation</h2>
          <p className="mb-4 text-sm text-muted">
            Endpunkte, Datenformate und Berechtigungen für die Integration.
          </p>
          <a
            className="button-secondary"
            href={`${import.meta.env.VITE_API_URL ?? '/api'}/docs`}
            target="_blank"
            rel="noreferrer"
          >
            Dokumentation öffnen
            <ExternalLink size={14} />
          </a>
        </section>
      </div>
    </>
  );
}
